import styles from './styles.module.scss';
import cn from 'classnames';
import { useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { DateTimeFormats } from 'shared/lib';
import { formatDate } from 'utils/formats';

type DateRangeProps = {
  fromDate?: string;
  toDate?: string;
  fullWidth?: boolean;
  placeholder?: string;
  onDateChange?: (from: string, to: string) => void;
};

export const DateRange = ({ fromDate, toDate, fullWidth, placeholder = 'Period...', onDateChange }: DateRangeProps) => {
  const refComp = useRef<Flatpickr>(null);
  const [hasSelectedDates, setHasSelectedDates] = useState(!!(fromDate || toDate));

  const handleDateChange = (dates: Date[]) => {
    const hasDates = dates.length > 0;
    setHasSelectedDates(hasDates);

    if (dates.length === 2 && onDateChange) {
      const format = (date: Date) => formatDate(date, DateTimeFormats.DateISO); // YYYY-MM-DD
      onDateChange(format(dates[0]), format(dates[1]));
    } else if (dates.length === 0 && onDateChange) {
      onDateChange('', '');
    }
  };

  const getDefaultDate = () => {
    const dates: Date[] = [];
    if (fromDate) {
      const date = new Date(fromDate);
      if (!isNaN(date.getTime())) dates.push(date);
    }
    if (toDate) {
      const date = new Date(toDate);
      if (!isNaN(date.getTime())) dates.push(date);
    }
    return dates;
  };

  const openCalendar = () => {
    refComp.current?.flatpickr.open();
  };

  const clearCalendar = () => {
    refComp.current?.flatpickr.clear();
    setHasSelectedDates(false);
  };

  return (
    <div className={cn('position-relative')}>
      <Flatpickr
        ref={refComp}
        placeholder={placeholder}
        className={cn('form-control form-control-sm form-control-solid pe-14', styles.dateRangeInput, {
          [styles.fullWidth]: fullWidth
        })}
        style={{ height: '43px' }}
        onChange={handleDateChange}
        options={{
          disableMobile: true,
          mode: 'range',
          defaultDate: getDefaultDate(),
          dateFormat: 'd/m/Y',
          locale: { firstDayOfWeek: 1 },
          onReady: (_, __, instance) => {
            instance.calendarContainer.classList.add('av-flatpickr-calendar');
          }
        }}
      />
      <div className="av-calendar-icon d-flex align-items-center gap-2">
        <i
          className="ki-duotone ki-calendar fs-2"
          style={{ cursor: 'pointer' }}
          onClick={openCalendar}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && openCalendar()}
        >
          <span className="path1"></span>
          <span className="path2"></span>
        </i>

        {hasSelectedDates && (
          <i
            className="ki-duotone ki-eraser fs-2"
            style={{ cursor: 'pointer' }}
            onClick={clearCalendar}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && clearCalendar()}
            title="Clear dates"
          >
            <span className="path1"></span>
            <span className="path2"></span>
            <span className="path3"></span>
          </i>
        )}
      </div>
    </div>
  );
};
